// Routes.tsx
import React, { lazy } from "react";
import { Navigate, useRoutes } from "react-router-dom";
import Permissions from "src/common_constants/Permission";
import { usePermission } from "src/context/PermissionContext";
import CustomLoader from "./shared_components/CustomLoader";

interface RoutesProps {}

const Dashboard = lazy(() => import("src/pages/Dashboard"));
const Reservation = lazy(() => import("src/pages/Reservation"));
const Contract = lazy(() => import("src/pages/Contract"));
const Asset = lazy(() => import("src/pages/Asset"));
const Customer = lazy(() => import("src/pages/Customer"));

const Calendar = lazy(() => import("src/pages/Calendar"));

const Settings = lazy(() => import("src/pages/Settings"));

//Start user management
const Profile = lazy(
  () => import("src/components/settings/user-management/Profile")
);
const UserManagementForm = lazy(
  () => import("src/components/settings/user-management/UserManagementForm")
);
const UserManagementGrid = lazy(
  () => import("src/components/settings/user-management/UserManagementGrid")
);
const UserView = lazy(
  () => import("src/components/settings/user-management/UserView")
);
//end user management

const SettingsLocationManagement = lazy(
  () => import("src/components/settings/location-management/index")
);

const SettingVehicleModelManagement = lazy(
  () => import("src/components/settings/Asset-management-Model/index")
);

const SettingVehicleTypeManagement = lazy(
  () => import("src/components/settings/Asset-management-Type/index")
);

const SettingVehicleOptionManagement = lazy(
  () => import("src/components/settings/Asset-management-Option/index")
);

const SettingsInsuranceManagement = lazy(
  () => import("src/components/settings/insurance-management/index")
);

const SettingsVechicleMakeManagement = lazy(
  () => import("src/components/settings/Asset-management-Make/index")
);

//Start asset rate name management
const RateNameManagementForm = lazy(
  () =>
    import(
      "src/components/settings/rate-name-management/RateNameManagementForm"
    )
);
const RateNameManagementGrid = lazy(
  () =>
    import(
      "src/components/settings/rate-name-management/RateNameManagementGrid"
    )
);

const RatePlanManagementGrid = lazy(
  () =>
    import(
      "src/components/settings/rate-plan-management/RatePlanManagementGrid"
    )
);

const RatePlanManagementForm = lazy(
  () =>
    import(
      "src/components/settings/rate-plan-management/RatePlanManagementForm"
    )
);

const RatePlanView = lazy(
  () => import("src/components/settings/rate-plan-management/RatePlanView")
);

//end asset rate name management

//start add on charges

const AddOnCharge = lazy(
  () => import("src/components/settings/add-on-charges/AddOnChargesGrid")
);
const AddOnForm = lazy(
  () => import("src/components/settings/add-on-charges/AddOnChargesForm")
);
const AddOnView = lazy(
  () => import("src/components/settings/add-on-charges/AddOnCharges")
);
//end add on charges

//start client settings
const ClientView = lazy(
  () => import("src/components/settings/ClientSettings/Client-Profile/Profile")
);
const CilentEditView = lazy(
  () =>
    import(
      "src/components/settings/ClientSettings/Client-Profile/ClientEditForm"
    )
);

const TaxManagementForm = lazy(
  () => import("src/components/settings/ClientSettings/Tax/TaxManagementForm")
);
const TaxManagementGrid = lazy(
  () => import("src/components/settings/ClientSettings/Tax/TaxManagementGrid")
);

//end client settings

//start Reports
const ReportsPage = lazy(() => import("src/pages/Reports"));
const Report = lazy(() => import("src/components/reports/components/Report"));

//end Reports

const Routes: React.FC<RoutesProps> = () => {
  //Start Customer
  const canViewCustomer = usePermission(Permissions.CustomerView);
  const canCreateCustomer = usePermission(Permissions.CustomerCreate);
  const canUpdateCustomer = usePermission(Permissions.CustomerUpdate);
  //End Customer

  //Start Location
  const canViewLocation = usePermission(Permissions.LocationView);
  const canCreateLocation = usePermission(Permissions.LocationCreate);
  const canUpdateLocation = usePermission(Permissions.LocationUpdate);
  //End Location

  //Start Insurance
  const canViewInsurance = usePermission(Permissions.InsuranceProviderView);
  const canCreateInsurance = usePermission(
    Permissions.InsuranceProviderCreateModify
  );
  const canUpdateInsurance = usePermission(
    Permissions.InsuranceProviderCreateModify
  );
  //End Insurance

  //Start User
  const canViewProfile = usePermission(Permissions.UserViewSelf);
  const canUserCreate = usePermission(Permissions.UserCreate);
  const canUserUpdate = usePermission(Permissions.UserUpdate);
  const canViewUsersGrid = usePermission(Permissions.UserView);
  const canUpdateUserSelf = usePermission(Permissions.UserUpdateSelf);

  //End USer

  //Start Assest
  const canViewAsset = usePermission(Permissions.AssetView);
  const canCreateAsset = usePermission(Permissions.AssetCreate);
  const canUpdateAsset = usePermission(Permissions.AssetUpdate);
  const canViewAssestMake = usePermission(Permissions.AssetMakeView);
  const canViewAssestModel = usePermission(Permissions.AssetModelView);
  const canViewAssestType = usePermission(Permissions.AssetTypeView);
  const canViewAssestOption = usePermission(Permissions.AssetOptionView);

  //End Assest

  //start rate management
  const canViewAssetRate = usePermission(Permissions.AssetRateCreateModify);
  const canCreateUpdateAssetRate = usePermission(
    Permissions.AssetRateDeleteRestore
  );
  //end rate management

  //start client settings
  const canViewTax = usePermission(Permissions.AssetRateCreateModify);
  const canCreateAndModifyTax = usePermission(
    Permissions.AssetRateCreateModify
  );
  const canViewClientProfile = usePermission(Permissions.AssetRateCreateModify);
  const canCreateAndModifylientProfile = usePermission(
    Permissions.AssetRateCreateModify
  );
  //end rate management

  //Start reservation
  const canViewReservation = usePermission(Permissions.BookingView);
  const canCreateModifyReservation = usePermission(
    Permissions.BookingCreateModify
  );
  //End reservation

  let routes = useRoutes([
    {
      path: "/",
      children: [
        {
          path: "Dashboard",
          element: <Dashboard />,
        },

        {
          path: "/",
          element: <Navigate to="/Dashboard" replace />,
        },

        { path: "Calendar", element: <Calendar /> },

        {
          path: "Reservation",
          children: [
            {
              path: "",
              element: canViewReservation ? (
                <Reservation />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Add-Reservation",
              element: canCreateModifyReservation ? (
                <Reservation route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Edit-Reservation",
              element: canCreateModifyReservation ? (
                <Reservation route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "View-Reservation",
              element: canViewReservation ? (
                <Reservation route="view" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
          ],
        },

        {
          path: "Contract",
          children: [
            {
              path: "",
              element: canViewReservation ? (
                <Contract />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Add-Contract",
              element: canCreateModifyReservation ? (
                <Contract route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Edit-Contract",
              element: canCreateModifyReservation ? (
                <Contract route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "View-Contract",
              element: canViewReservation ? (
                <Contract route="view" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Sign-Contract",
              element: canCreateModifyReservation ? (
                <Contract route="sign" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
          ],
        },

        {
          path: "Asset",
          children: [
            {
              path: "",
              element: canViewAsset ? (
                <Asset />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Add-Asset",
              element: canCreateAsset ? (
                <Asset route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Edit-Asset",
              element: canUpdateAsset ? (
                <Asset route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "View-Asset",
              element: canViewAsset ? (
                <Asset route="view" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
          ],
        },

        {
          path: "Customer",
          children: [
            {
              path: "",
              element: canViewCustomer ? (
                <Customer />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Add-Customer",
              element: canCreateCustomer ? (
                <Customer route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "Edit-Customer",
              element: canUpdateCustomer ? (
                <Customer route="form" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
            {
              path: "View-Customer",
              element: canViewCustomer ? (
                <Customer route="view" />
              ) : (
                <Navigate to="/Access-Denied" replace={true} />
              ),
            },
          ],
        },

        {
          path: "Settings",
          element: <Settings />,
          children: [
            {
              path: "",
              element: <Navigate to="Location" replace={true} />,
            },

            {
              path: "Location",
              children: [
                {
                  path: "",
                  element: canViewLocation ? (
                    <SettingsLocationManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "Add-Location",
                  element: canCreateLocation ? (
                    <SettingsLocationManagement route="form" />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "Edit-Location",
                  element: canUpdateLocation ? (
                    <SettingsLocationManagement route="form" />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "View-Location",
                  element: canViewLocation ? (
                    <SettingsLocationManagement route="view" />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
              ],
            },

            {
              path: "Insurance",
              children: [
                {
                  path: "",
                  element: canViewInsurance ? (
                    <SettingsInsuranceManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "Add-Insurance",
                  element: canCreateInsurance ? (
                    <SettingsInsuranceManagement route="form" />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "Edit-Insurance",
                  element: canUpdateInsurance ? (
                    <SettingsInsuranceManagement route="form" />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "View-Insurance",
                  element: canViewInsurance ? (
                    <SettingsInsuranceManagement route="view" />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
              ],
            },

            {
              path: "User",
              children: [
                {
                  path: "",
                  element: canViewUsersGrid ? (
                    <UserManagementGrid />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "Add-User",
                  element: canUserCreate ? (
                    <UserManagementForm />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "Edit-User",
                  element: canUserUpdate ? (
                    <UserManagementForm />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "View-User",
                  element: canViewUsersGrid ? (
                    <UserView />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "Profile",
                  element: canViewProfile ? (
                    <Profile />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "Edit-Profile",
                  element: canUpdateUserSelf ? (
                    <UserManagementForm />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
              ],
            },

            {
              path: "Asset",
              children: [
                {
                  path: "AssetMake",
                  element: canViewAssestMake ? (
                    <SettingsVechicleMakeManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "AssetModel",
                  element: canViewAssestModel ? (
                    <SettingVehicleModelManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "AssetType",
                  element: canViewAssestType ? (
                    <SettingVehicleTypeManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
                {
                  path: "AssetOption",
                  element: canViewAssestOption ? (
                    <SettingVehicleOptionManagement />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
              ],
            },
            {
              path: "Rate",
              children: [
                {
                  path: "RateName",
                  children: [
                    {
                      path: "",
                      element: canViewAssetRate ? (
                        <RateNameManagementGrid />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "Add-RateName",
                      element: canCreateUpdateAssetRate ? (
                        <RateNameManagementForm />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "Edit-RateName",
                      element: canCreateUpdateAssetRate ? (
                        <RateNameManagementForm />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "RatePlan",
                  children: [
                    {
                      path: "",
                      element: canViewAssetRate ? (
                        <RatePlanManagementGrid />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "Add-RatePlan",
                      element: canCreateUpdateAssetRate ? (
                        <RatePlanManagementForm />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "Edit-RatePlan",
                      element: canCreateUpdateAssetRate ? (
                        <RatePlanManagementForm />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "View-RatePlan",
                      element: canViewAssetRate ? (
                        <RatePlanView />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "AddOnCharge",
                  children: [
                    {
                      path: "",
                      element: canViewAssetRate ? (
                        <AddOnCharge />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "Add-AddOnCharge",
                      element: canCreateUpdateAssetRate ? (
                        <AddOnForm />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "Edit-AddOnCharge",
                      element: canCreateUpdateAssetRate ? (
                        <AddOnForm />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "View-AddOnCharge",
                      element: canViewAssetRate ? (
                        <AddOnView />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
              ],
            },
            {
              path: "Client",
              children: [
                {
                  path: "Tax",
                  children: [
                    {
                      path: "",
                      element: canViewTax ? (
                        <TaxManagementGrid />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "Add-Tax",
                      element: canCreateAndModifyTax ? (
                        <TaxManagementForm />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                    {
                      path: "Edit-Tax",
                      element: canCreateAndModifyTax ? (
                        <TaxManagementForm />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "Profile",
                  children: [
                    {
                      path: "",
                      element: canViewClientProfile ? (
                        <ClientView />
                      ) : (
                        <Navigate to="/Access-Denied" replace={true} />
                      ),
                    },
                  ],
                },
                {
                  path: "Edit-Profile",
                  element: canCreateAndModifylientProfile ? (
                    <CilentEditView />
                  ) : (
                    <Navigate to="/Access-Denied" replace={true} />
                  ),
                },
              ],
            },
          ],
        },

        {
          path: "Reports",
          element: <ReportsPage />,
          children: [
            {
              path: "View/:id",
              element: <Report />,
            },
          ],
        },
      ],
    },
    {
      path: "Access-Denied",
      element: <>Access Denied!</>,
    },
    {
      path: "*",
      element: <CustomLoader />,
    },
  ]);

  return routes;
};
export default Routes;
